<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import Avatar from "./Avatar.vue";
import Button from "./Button.vue";
import Icon from "./Icon.vue";
import IconButton from "./IconButton.vue";
import SearchField from "./SearchField.vue";
import TagColorPicker from "./TagColorPicker.vue";
import { tagsApi, type TagRow } from "../api/organization";
import { contactsApi } from "../api/contacts";
import { errorText } from "../api/errors";
import { toast } from "../composables/feedback";
import { profileTone } from "../composables/profileColors";
import { DEFAULT_TAG_COLOR } from "../composables/tagColors";
import { TAG_SUGGESTIONS, tagNameTaken } from "../composables/tagLists";
import { useEscapeLayer } from "../composables/escape";
import { localized, t, type MessageKey } from "../i18n";

/**
 * A tag's page over the conversation list, as WhatsApp Web's "Create new list": name and colour,
 * ready suggestions while the name is empty, and the people in it. A new tag is created with its
 * people at once; on an existing one, people added or taken out change when it is saved.
 */
export interface TagPerson {
  id: string;
  name: string;
  address: string | null;
}

const props = defineProps<{
  slug: string;
  /** The tag being edited; null creates one. */
  tag: TagRow | null;
  /** Every tag, to keep names unique. */
  existing: TagRow[];
  /** People already in a new tag (the conversation it was created from). */
  preset?: TagPerson[];
}>();
const emit = defineEmits<{ back: []; saved: [tag: TagRow | null] }>();

const name = ref(props.tag ? localized(props.tag.name) : "");
const color = ref(props.tag?.color ?? DEFAULT_TAG_COLOR);
const people = ref<TagPerson[]>([...(props.preset ?? [])]);
const before = ref<Set<string>>(new Set());
const busy = ref(false);
const colorOpen = ref(false);
const picking = ref(false);

onMounted(async () => {
  if (!props.tag) return;
  try {
    const r = await contactsApi.list(props.slug, {
      tag: props.tag.id,
      limit: 100,
    });
    people.value = r.contacts.map((c) => ({
      id: c.id,
      name: c.displayName,
      address: c.address,
    }));
    before.value = new Set(people.value.map((p) => p.id));
  } catch (err) {
    toast(errorText(err));
  }
});

const taken = computed(() =>
  tagNameTaken(name.value, props.existing, props.tag?.id),
);
const suggestions = computed(() =>
  props.tag || name.value.trim()
    ? []
    : TAG_SUGGESTIONS.filter(
        (s) => !tagNameTaken(t(s.key as MessageKey), props.existing),
      ),
);
const ready = computed(() => !!name.value.trim() && !taken.value);

function suggest(s: (typeof TAG_SUGGESTIONS)[number]) {
  name.value = t(s.key as MessageKey);
  color.value = s.color;
}
function remove(id: string) {
  people.value = people.value.filter((p) => p.id !== id);
}

async function save() {
  if (!ready.value || busy.value) return;
  busy.value = true;
  try {
    const label = name.value.trim();
    if (!props.tag) {
      const r = await tagsApi.create(props.slug, {
        name: label,
        color: color.value,
        contactIds: people.value.map((p) => p.id),
      });
      toast(t("tags.editor.created"));
      emit("saved", r.tag);
      return;
    }
    const tag = props.tag;
    if (label !== localized(tag.name) || color.value !== tag.color)
      await tagsApi.patch(props.slug, tag.id, {
        name: label,
        color: color.value,
      });
    const now = new Set(people.value.map((p) => p.id));
    for (const id of now)
      if (!before.value.has(id))
        await tagsApi.tagContact(props.slug, id, tag.id, true);
    for (const id of before.value)
      if (!now.has(id)) await tagsApi.tagContact(props.slug, id, tag.id, false);
    toast(t("tags.editor.saved"));
    emit("saved", { ...tag, name: label, color: color.value });
  } catch (err) {
    toast(errorText(err));
  } finally {
    busy.value = false;
  }
}

async function archive() {
  const tag = props.tag;
  if (!tag || busy.value) return;
  busy.value = true;
  try {
    await tagsApi.patch(props.slug, tag.id, { isActive: false });
    toast(t("tags.editor.archived"), () => {
      void tagsApi
        .patch(props.slug, tag.id, { isActive: true })
        .then(() => emit("saved", tag))
        .catch((err) => toast(errorText(err)));
    });
    emit("saved", null);
  } catch (err) {
    toast(errorText(err));
  } finally {
    busy.value = false;
  }
}

// ---- adding people: a search over the contacts, with a box per person
const query = ref("");
const found = ref<TagPerson[]>([]);
const chosen = ref<Map<string, TagPerson>>(new Map());
let searchTimer: ReturnType<typeof setTimeout> | null = null;
async function searchPeople() {
  try {
    const r = await contactsApi.list(props.slug, { q: query.value, limit: 30 });
    found.value = r.contacts.map((c) => ({
      id: c.id,
      name: c.displayName,
      address: c.address,
    }));
  } catch (err) {
    toast(errorText(err));
  }
}
watch(query, () => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => void searchPeople(), 250);
});
function openPicker() {
  chosen.value = new Map(people.value.map((p) => [p.id, p]));
  query.value = "";
  picking.value = true;
  void searchPeople();
}
function toggle(p: TagPerson) {
  const next = new Map(chosen.value);
  if (next.has(p.id)) next.delete(p.id);
  else next.set(p.id, p);
  chosen.value = next;
}
function confirmPicker() {
  people.value = [...chosen.value.values()];
  picking.value = false;
}
// Above the tag's page: Escape leaves the people search, then closes the colours.
useEscapeLayer(
  () => picking.value,
  () => (picking.value = false),
);
useEscapeLayer(
  () => colorOpen.value,
  () => (colorOpen.value = false),
);
</script>
<template>
  <section v-if="!picking" class="side-panel" :aria-label="t('tags.title')">
    <header class="panel-header side-panel-header">
      <IconButton icon="back" :label="t('common.back')" @click="emit('back')" />
      <h2 class="grow">
        {{ t(tag ? "tags.editor.edit" : "tags.editor.new") }}
      </h2>
    </header>
    <div class="scroll side-panel-body">
      <div class="tag-editor-field">
        <label class="tag-editor-name" :class="{ error: taken }">
          <span>{{ t("tags.editor.name") }}</span>
          <span class="tag-editor-input">
            <input
              v-model="name"
              maxlength="60"
              :placeholder="t('tags.editor.name')"
              @keydown.enter="save"
            /><button
              type="button"
              class="tag-editor-color"
              :style="{ '--tag': color }"
              :aria-label="t('tags.editor.color')"
              :aria-expanded="colorOpen"
              @click="colorOpen = !colorOpen"
            />
          </span>
        </label>
        <small v-if="taken" class="danger">{{ t("tags.editor.exists") }}</small>
        <Transition name="pop"
          ><div v-if="colorOpen" class="tag-editor-palette">
            <TagColorPicker
              :model-value="color"
              @update:model-value="
                (c) => {
                  color = c;
                  colorOpen = false;
                }
              "
            /></div
        ></Transition>
      </div>

      <template v-if="suggestions.length">
        <h3 class="side-panel-caption">{{ t("tags.editor.suggestions") }}</h3>
        <button
          v-for="s in suggestions"
          :key="s.key"
          type="button"
          class="tag-panel-row compact"
          @click="suggest(s)"
        >
          <i class="tag-panel-dot" :style="{ '--tag': s.color }" /><span
            class="tag-panel-name grow"
            >{{ t(s.key as MessageKey) }}</span
          >
        </button>
      </template>

      <h3 class="side-panel-caption">{{ t("tags.editor.people") }}</h3>
      <button type="button" class="side-panel-add" @click="openPicker">
        <span class="side-panel-add-icon"
          ><Icon name="wa-plus" :size="24" /></span
        >{{ t("tags.editor.add_people") }}
      </button>
      <div v-for="p in people" :key="p.id" class="tag-person">
        <Avatar
          :name="p.name"
          :tone="profileTone(p.address, p.id)"
          :size="48"
        />
        <span class="grow tag-person-text">
          <span class="truncate">{{ p.name }}</span>
          <small v-if="p.address" class="muted truncate">{{ p.address }}</small>
        </span>
        <IconButton
          icon="trash"
          class="danger"
          :label="t('tags.editor.remove_person', { name: p.name })"
          @click="remove(p.id)"
        />
      </div>

      <div class="side-panel-actions">
        <Button :loading="busy" :disabled="!ready" @click="save">{{
          t(tag ? "common.save" : "tags.editor.create")
        }}</Button>
      </div>
      <button
        v-if="tag"
        type="button"
        class="list-row danger"
        :disabled="busy"
        @click="archive"
      >
        <Icon name="archive" /><span class="grow"
          >{{ t("tags.editor.archive") }}
          <span class="sub">{{ t("tags.editor.archive_hint") }}</span></span
        >
      </button>
    </div>
  </section>

  <section v-else class="side-panel" :aria-label="t('tags.editor.add_people')">
    <header class="panel-header side-panel-header">
      <IconButton
        icon="back"
        :label="t('common.back')"
        @click="picking = false"
      />
      <h2 class="grow">{{ t("tags.editor.add_people") }}</h2>
    </header>
    <div class="side-search">
      <SearchField v-model="query" :placeholder="t('tags.picker.search')" />
    </div>
    <div class="scroll side-panel-body">
      <button
        v-for="p in found"
        :key="p.id"
        type="button"
        class="list-row tag-pick-row"
        :aria-pressed="chosen.has(p.id)"
        @click="toggle(p)"
      >
        <Avatar :name="p.name" :tone="profileTone(p.address, p.id)" />
        <span class="grow tag-person-text">
          <span class="truncate">{{ p.name }}</span>
          <small v-if="p.address" class="muted truncate">{{ p.address }}</small>
        </span>
        <input
          type="checkbox"
          class="wa-check"
          :checked="chosen.has(p.id)"
          tabindex="-1"
          aria-hidden="true"
        />
      </button>
      <div v-if="!found.length" class="empty-compact">
        <Icon name="search" :size="40" />
        <p>{{ t("tags.picker.empty") }}</p>
      </div>
    </div>
    <footer class="side-footer">
      <Button icon="check" @click="confirmPicker">{{
        t("tags.picker.add", { count: chosen.size })
      }}</Button>
    </footer>
  </section>
</template>
